'use client';

import React from 'react';
import { Alert, AlertTitle, Stack, Typography } from '@unity/cloud-ui';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';

export interface AdmonitionProps {
  title: string;
  severity: 'info' | 'success' | 'warning' | 'error';
}

export const Admonition = (props: React.PropsWithChildren<AdmonitionProps>) => {
  const { title, severity, children } = props;

  const noStringChildren = React.Children.toArray(children).every((child) => typeof child !== 'string');

  return (
    <Alert
      severity={severity}
      slotProps={{
        icon: { 'aria-hidden': true },
      }}
    >
      <AlertTitle>{title}</AlertTitle>
      <Typography component="span">{noStringChildren ? <Stack gap={1}>{children}</Stack> : children}</Typography>
    </Alert>
  );
};

const documentationUrl = getComponentDocumentationUrl('admonitions');

const descriptions = {
  children: 'The content of the Admonition. Can be anything.',
  schema: `Creates an Admonition to highlight important information. Admonitions are called as one of the following: 

  - \\<Important\\>
  - \\<Note\\>
  - \\<Tip\\>
  - \\<Warning\\>
  
  The Color and the icon will change according to the type of Admonition.

  \n**Example**\n\`\`\`mdx\n<Important>\n  The content of the Admonition\n</Important>\n\`\`\`
  \n${documentationUrl}`,
};

export const admonitionSchema = z
  .object({
    children: z.any().describe(descriptions.children),
  })
  .describe(descriptions.schema);
